import React from 'react'
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs'

const PORTAL = {
  primary: '#223843',
  primaryLight: 'rgba(34, 56, 67, 0.08)',
  border: '#E5E7EB',
  text: '#111827',
  textMuted: '#6B7280',
  textDisabled: '#9CA3AF',
  white: '#ffffff',
  font: 'Inter, system-ui, sans-serif',
}

function getVisiblePages(totalPages, currentPage, maxVisiblePages = 7) {
  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages = [1]
  let start = Math.max(2, currentPage - Math.floor((maxVisiblePages - 3) / 2))
  let end = Math.min(totalPages - 1, start + maxVisiblePages - 3)

  if (end >= totalPages - 1) {
    start = Math.max(2, totalPages - maxVisiblePages + 2)
  }

  if (start > 2) {
    pages.push('ellipsis-start')
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  if (end < totalPages - 1) {
    pages.push('ellipsis-end')
  }

  pages.push(totalPages)
  return pages
}

const NavButton = ({ disabled, onClick, ariaLabel, children }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={onClick}
    className="table-pagination-nav-btn"
    style={{
      backgroundColor: disabled ? 'transparent' : PORTAL.white,
      color: disabled ? PORTAL.textDisabled : PORTAL.primary,
      cursor: disabled ? 'not-allowed' : 'pointer',
      boxShadow: disabled ? 'none' : '0 1px 2px rgba(34, 56, 67, 0.06)',
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.backgroundColor = PORTAL.primary
        e.currentTarget.style.color = PORTAL.white
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 56, 67, 0.2)'
      }
    }}
    onMouseLeave={(e) => {
      if (!disabled) {
        e.currentTarget.style.backgroundColor = PORTAL.white
        e.currentTarget.style.color = PORTAL.primary
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(34, 56, 67, 0.06)'
      }
    }}
  >
    {children}
  </button>
)

const PageButton = ({ page, isActive, onClick }) => (
  <button
    type="button"
    aria-label={`Page ${page}`}
    aria-current={isActive ? 'page' : undefined}
    onClick={onClick}
    className="table-pagination-page-btn"
    style={{
      backgroundColor: isActive ? PORTAL.primary : 'transparent',
      color: isActive ? PORTAL.white : PORTAL.text,
      fontWeight: isActive ? 700 : 500,
      boxShadow: isActive ? '0 4px 12px rgba(34, 56, 67, 0.25)' : 'none',
    }}
    onMouseEnter={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = PORTAL.primaryLight
        e.currentTarget.style.color = PORTAL.primary
      }
    }}
    onMouseLeave={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = 'transparent'
        e.currentTarget.style.color = PORTAL.text
      }
    }}
  >
    {page}
  </button>
)

const TablePagination = ({
  currentPage,
  setCurrentPage,
  totalPages = 0,
  indexOfFirstItem,
  indexOfLastItem,
  totalItems = 0,
  itemLabel = '',
  className = 'mb-3 mt-4',
  wrapperStyle = {},
  summaryStyle = {},
  paginationClassName = '',
  paginationStyle = {},
  maxVisiblePages = 7,
  paginationScrollable = false,
}) => {
  const safeTotalPages = Math.max(totalPages || 0, 0)
  const start = totalItems > 0 ? indexOfFirstItem + 1 : 0
  const end = Math.min(indexOfLastItem, totalItems)
  const isPrevDisabled = currentPage === 1
  const isNextDisabled = currentPage === safeTotalPages || safeTotalPages === 0
  const labelSuffix = itemLabel ? ` ${itemLabel}` : ''
  const visibleLimit = maxVisiblePages || safeTotalPages

  const pageNumbers = getVisiblePages(safeTotalPages, currentPage, visibleLimit)

  const pagination = (
    <nav
      aria-label="Table pagination"
      className={`table-pagination-nav ${paginationClassName}`.trim()}
      style={paginationStyle}
    >
      <NavButton
        ariaLabel="Previous page"
        disabled={isPrevDisabled}
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
      >
        <BsChevronLeft size={14} />
      </NavButton>

      {pageNumbers.map((page, idx) => {
        if (page === 'ellipsis-start' || page === 'ellipsis-end') {
          return (
            <span
              key={`ellipsis-${idx}`}
              aria-hidden="true"
              className="table-pagination-ellipsis"
            >
              ···
            </span>
          )
        }

        return (
          <PageButton
            key={page}
            page={page}
            isActive={page === currentPage}
            onClick={() => setCurrentPage(page)}
          />
        )
      })}

      <NavButton
        ariaLabel="Next page"
        disabled={isNextDisabled}
        onClick={() =>
          setCurrentPage((prev) => Math.min(prev + 1, safeTotalPages))
        }
      >
        <BsChevronRight size={14} />
      </NavButton>
    </nav>
  )

  return (
    <>
      <style>
        {`
          .table-pagination-root {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            padding: 12px 16px;
            border-radius: 12px;
          }

          .table-pagination-summary {
            flex: 1 1 200px;
            min-width: 0;
            font-size: 13px;
            color: ${PORTAL.textMuted};
            font-family: ${PORTAL.font};
            font-weight: 500;
            line-height: 1.5;
          }

          .table-pagination-controls {
            flex: 0 1 auto;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            min-width: 0;
            max-width: 100%;
          }

          .table-pagination-controls--scrollable {
            overflow-x: auto;
            overflow-y: hidden;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
          }

          .table-pagination-nav {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 4px;
            padding: 4px;
            border-radius: 12px;
            border: 1px solid ${PORTAL.border};
            max-width: 100%;
            box-sizing: border-box;
          }

          .table-pagination-nav-btn,
          .table-pagination-page-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            min-width: 36px;
            padding: 0 8px;
            border: none;
            border-radius: 10px;
            font-family: ${PORTAL.font};
            font-size: 13px;
            transition: all 0.2s ease;
            flex-shrink: 0;
          }

          .table-pagination-ellipsis {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 24px;
            height: 36px;
            color: ${PORTAL.textMuted};
            font-family: ${PORTAL.font};
            font-size: 14px;
            font-weight: 600;
            letter-spacing: 1px;
            user-select: none;
            flex-shrink: 0;
          }

          @media (max-width: 768px) {
            .table-pagination-root {
              flex-direction: column !important;
              align-items: center !important;
              flex-wrap: wrap !important;
              padding: 12px 8px;
              gap: 10px;
            }

            .table-pagination-summary {
              flex: 0 0 auto;
              width: 100%;
              text-align: center;
              font-size: 12px;
            }

            .table-pagination-controls {
              width: 100%;
              justify-content: center;
              flex: 0 0 auto;
            }

            .table-pagination-controls--scrollable {
              overflow-x: visible;
            }

            .table-pagination-nav {
              width: auto;
              max-width: 100%;
            }

            .table-pagination-nav-btn,
            .table-pagination-page-btn {
              width: 34px;
              height: 34px;
              min-width: 34px;
              font-size: 12px;
            }

            .table-pagination-ellipsis {
              height: 34px;
              min-width: 20px;
              font-size: 13px;
            }
          }

          @media (max-width: 380px) {
            .table-pagination-root {
              padding: 10px 4px;
            }

            .table-pagination-summary {
              font-size: 11px;
            }

            .table-pagination-nav {
              gap: 2px;
              padding: 3px;
            }

            .table-pagination-nav-btn,
            .table-pagination-page-btn {
              width: 32px;
              height: 32px;
              min-width: 32px;
              border-radius: 8px;
            }

            .table-pagination-ellipsis {
              height: 32px;
              min-width: 16px;
            }
          }
        `}
      </style>

      <div
        className={`table-pagination-root ${className}`.trim()}
        style={wrapperStyle}
      >
        <div className="table-pagination-summary" style={summaryStyle}>
          Showing{' '}
          <span style={{ color: PORTAL.primary, fontWeight: 700 }}>{start}</span>
          {' – '}
          <span style={{ color: PORTAL.primary, fontWeight: 700 }}>{end}</span>
          {' of '}
          <span style={{ color: PORTAL.primary, fontWeight: 700 }}>
            {totalItems}
          </span>
          {labelSuffix && (
            <span style={{ color: PORTAL.textMuted }}>{labelSuffix}</span>
          )}
        </div>

        {safeTotalPages > 0 && (
          <div
            className={
              paginationScrollable
                ? 'table-pagination-controls table-pagination-controls--scrollable'
                : 'table-pagination-controls'
            }
          >
            {pagination}
          </div>
        )}
      </div>
    </>
  )
}

export default TablePagination